import { computed, observable, reaction, runInAction } from "mobx"

import { getFullName } from "@/hooks/useProfile.ts"
import { batchInfo, emptyBatch } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { formatDate } from "@/lib/date.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { toProfileUsername } from "@/lib/std.ts"
import { tasksByRepoKey, tasksByUserKey } from "@/routes/tasks/tasksHooks.ts"

import type { AiAgentTask, Batch, GitHubPrInfo, TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("UserProfileModel")

export class UserProfileModel extends Model {
  private profile: IObservableValue<TD_MemberProfile | null> = observable.box(null)
  private readonly tasksPromise: Promise<void>

  private readonly batch: IObservableValue<Batch<AiAgentTask>> = observable.box(emptyBatch())
  readonly tasks: IObservableValue<TaskGroup[]> = observable.box([])

  readonly page: number
  readonly username: string
  readonly successful: boolean

  readonly gitHubPrs = observable.box<Map<string, GitHubPrInfo[]>>(new Map())

  private readonly hasFetchedData = observable.box(false)
  private readonly isFetching = observable.box(false)

  constructor(private readonly context: OrgContext, params: UserProfileModelParams) {
    super("UserProfileModel")

    this.page = params.page
    this.username = params.username
    this.successful = params.successful

    this.tasksPromise = this.refreshTasks()

    this.lifetime.add(reaction(
      () => ({
        isFetching: this.isFetching.get(),
        hasFetchedData: this.hasFetchedData.get(),
      }),
      ({ isFetching, hasFetchedData }) => {
        this.isLoading.set(isFetching && !hasFetchedData)
      }
    ), "UserProfileModel:isLoading")
  }

  async awaitReady() {
    try {
      await this.tasksPromise
    } catch {
      // ignore
    }
    return true
  }

  hasPrev = computed(() => {
    return this.page > 0
  })

  hasNext = computed(() => {
    return this.batch.get().next !== null && this.batch.get().next !== this.batch.get().totalCount?.toString()
  })

  hasMore = computed(() => this.hasPrev.get() || this.hasNext.get())

  fullName = computed(() => {
    const profile = this.profile.get()
    return profile ? getFullName(profile) : null
  })

  deleteTask = async (task: AiAgentTask) => {
    await withGlobalLoader(this.context, this.context.queries.apiClient.deleteTask(task.id))
    void this.context.queryClient.invalidateQueries(tasksByRepoKey(task.projectKey))

    const profile = this.profile.get()
    if (profile) {
      void this.context.queryClient.invalidateQueries(tasksByUserKey(profile.id))
    }

    await this.refreshTasks()
  }

  private async refreshTasks() {
    runInAction(() => {
      this.isFetching.set(true)
    })

    try {
      const profile = await this.context.queries.profile(toProfileUsername(this.username))
      runInAction(() => this.profile.set(profile))

      const batchSize = 25
      const nextBatch = batchInfo(batchSize, (this.page * batchSize).toString())

      const tasks = await this.context.queries.listTasksForUser(profile.id, this.successful, nextBatch)

      const groups: Record<string, AiAgentTask[]> = {}
      for (const task of tasks.data) {
        const date = formatDate(task.createdAt)
        groups[date] = groups[date] || []

        groups[date].push(task)
      }

      try {
        const prsMap = await this.subscribeToGitHubPrStatus(tasks.data)
        runInAction(() => this.gitHubPrs.set(prsMap))
      } catch (_) {
        // ignore errors
      }

      runInAction(() => {
        this.batch.set(tasks)
        this.tasks.set(Object.keys(groups).map(it => ({ date: it, tasks: groups[it] })))
        this.hasFetchedData.set(true)
      })
    } finally {
      runInAction(() => {
        this.isFetching.set(false)
      })
    }
  }

  private async subscribeToGitHubPrStatus(tasks: AiAgentTask[]): Promise<Map<string, GitHubPrInfo[]>> {
    try {
      const taskIds = tasks.map(t => t.id)
      if (taskIds.length === 0) return new Map()

      // Tasks list is refreshed every minute, we can ignore the subscription here
      const subscription = await withGlobalLoader(this.context, this.context.queries.subscribeToOpenedPullRequests(taskIds))

      const result = new Map<string, GitHubPrInfo[]>()
      for (const pair of subscription.initial) {
        result.set(pair.first as string, pair.second.sort((a, b) => a.number - b.number))
      }

      return result
    } catch (e) {
      log.error("Failed to subscribe to GitHub PRs status (profile):", e)
      return new Map()
    }
  }
}

type UserProfileModelParams = {
  page: number
  username: string
  successful: boolean
}

type TaskGroup = {
  date: string
  tasks: AiAgentTask[]
}